import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Building2, TrendingUp, Plus, Trash2 } from "lucide-react";

export default function TradelinesPanel({ clientId }) {
  const [providers, setProviders] = useState([]);
  const [tradelines, setTradelines] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ provider: "", bank_name: "", credit_limit: "", reported_age: "", price: "", notes: "" });
  const [pOpen, setPOpen] = useState(false);
  const [pSaving, setPSaving] = useState(false);
  const [pForm, setPForm] = useState({ name: "", url: "", description: "" });

  const load = () => {
    api.get("/tradeline-providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get(`/clients/${clientId}/tradelines`).then((r) => setTradelines(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [clientId]);

  const add = async () => {
    if (!form.provider || !form.bank_name) return toast.error("Provider and bank name required");
    setSaving(true);
    try {
      await api.post("/tradelines", { client_id: clientId, ...form, status: "Requested" });
      toast.success("Tradeline linked to client");
      setOpen(false);
      setForm({ provider: "", bank_name: "", credit_limit: "", reported_age: "", price: "", notes: "" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    await api.put(`/tradelines/${id}/status`, { status });
    load();
  };
  const remove = async (id) => { await api.delete(`/tradelines/${id}`); load(); };

  const addProvider = async () => {
    if (!pForm.name.trim()) return toast.error("Provider name is required");
    setPSaving(true);
    try {
      await api.post("/tradeline-providers", pForm);
      toast.success("Tradeline company added");
      setPOpen(false);
      setPForm({ name: "", url: "", description: "" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setPSaving(false); }
  };
  const removeProvider = async (id) => { await api.delete(`/tradeline-providers/${id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200/90 flex gap-3" data-testid="tradeline-disclaimer">
        <TrendingUp className="h-5 w-5 shrink-0 mt-0.5" />
        <p>Authorized-user tradelines add real, accurately-reported account history to a client's profile. All information reported to the bureaus must be truthful — this tool does not fabricate or backdate history beyond an account's genuine age.</p>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold">Linked Tradelines</h3>
        <Button onClick={() => setOpen(true)} className="rounded-lg" data-testid="add-tradeline-btn"><Sparkles className="h-4 w-4 mr-1" /> Link Tradeline</Button>
      </div>

      <div className="grid gap-3">
        {tradelines.map((t) => (
          <Card key={t.id} className="p-4 bg-card/60 border-border flex items-center justify-between" data-testid={`tradeline-${t.id}`}>
            <div>
              <div className="font-medium">{t.bank_name} <span className="text-muted-foreground text-sm">· {t.provider}</span></div>
              <div className="text-xs text-muted-foreground mt-1">Limit {t.credit_limit || "—"} · Age {t.reported_age || "—"} · {t.price || "—"}</div>
            </div>
            <div className="flex items-center gap-2">
              <select value={t.status} onChange={(e) => setStatus(t.id, e.target.value)} className="bg-secondary border border-border rounded-md text-xs px-2 py-1.5" data-testid={`tradeline-status-${t.id}`}>
                {["Requested", "Purchased", "Posted", "Reported", "Removed"].map((s) => <option key={s}>{s}</option>)}
              </select>
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(t.id)} data-testid={`delete-tradeline-${t.id}`}>Delete</Button>
            </div>
          </Card>
        ))}
        {!tradelines.length && <p className="text-sm text-muted-foreground py-4">No tradelines linked yet.</p>}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-semibold">Where to Buy Tradelines</h3>
          <Button size="sm" variant="secondary" onClick={() => setPOpen(true)} className="rounded-lg" data-testid="add-provider-btn"><Plus className="h-4 w-4 mr-1" /> Add Company</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {providers.map((p) => (
            <Card key={p.id || p.name} className="p-4 bg-card/60 border-border" data-testid={`provider-${p.id || p.name}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-medium"><Building2 className="h-4 w-4 text-primary" /> {p.name}</div>
                {p.custom && (
                  <button onClick={() => removeProvider(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-provider-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
              {p.description && <p className="text-xs text-muted-foreground mt-2">{p.description}</p>}
              <div className="flex items-center justify-between mt-3">
                <span className="text-[10px] uppercase tracking-widest text-primary font-semibold">{p.specialty || (p.custom ? "Custom provider" : "")}</span>
                {p.url
                  ? <a href={p.url.startsWith("http") ? p.url : `https://${p.url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline" data-testid={`provider-link-${p.id || p.name}`}>Visit site →</a>
                  : <span className="text-[10px] text-muted-foreground">No website</span>}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="bg-card/90 backdrop-blur-xl border-border" description="Add your own tradeline company with a website link">
          <DialogHeader><DialogTitle className="font-display">Add Tradeline Company</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <TF label="Company Name *" v={pForm.name} set={(v) => setPForm({ ...pForm, name: v })} testid="provider-name" />
            <TF label="Website URL" v={pForm.url} set={(v) => setPForm({ ...pForm, url: v })} placeholder="https://theircompany.com" testid="provider-url" />
            <div className="space-y-2">
              <Label>Short Description</Label>
              <Input value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} placeholder="e.g. My go-to seasoned AU tradelines" data-testid="provider-description" />
            </div>
          </div>
          <DialogFooter><Button onClick={addProvider} disabled={pSaving} className="rounded-lg" data-testid="save-provider-btn">{pSaving ? "Saving…" : "Add Company"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card/90 backdrop-blur-xl border-border">
          <DialogHeader><DialogTitle className="font-display">Link a Tradeline</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Provider</Label>
              <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm" data-testid="tl-provider">
                <option value="">Select provider…</option>
                {providers.map((p) => <option key={p.id || p.name}>{p.name}</option>)}
              </select>
            </div>
            <TF label="Bank / Card Issuer" v={form.bank_name} set={(v) => setForm({ ...form, bank_name: v })} testid="tl-bank" />
            <div className="grid grid-cols-2 gap-3">
              <TF label="Credit Limit" v={form.credit_limit} set={(v) => setForm({ ...form, credit_limit: v })} testid="tl-limit" />
              <TF label="Account Age" v={form.reported_age} set={(v) => setForm({ ...form, reported_age: v })} placeholder="e.g. 6 years" testid="tl-age" />
            </div>
            <TF label="Price" v={form.price} set={(v) => setForm({ ...form, price: v })} placeholder="$" testid="tl-price" />
          </div>
          <DialogFooter><Button onClick={add} disabled={saving} className="rounded-lg" data-testid="save-tradeline-btn">{saving ? "Saving…" : "Link Tradeline"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TF({ label, v, set, placeholder, testid }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input value={v} onChange={(e) => set(e.target.value)} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}
